import { useMemo, useState } from 'react'
import {
  useAbsenceTypes,
  useAbsences,
  useDeleteAbsence,
  useSaveAbsence,
  useUsers
} from '../api/queries'
import type { Absence, AbsenceInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'

/** yyyy-MM-dd for i dag, i lokal tid - ikke UTC, som ville skifte dag om aftenen. */
function today() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function addDays(iso: string, days: number) {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d + days)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function formatDate(value: string) {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** "12. jan - 19. jan" eller bare den ene dag. */
function formatPeriod(start: string, end: string) {
  return start === end ? formatDate(start) : `${formatDate(start)} - ${formatDate(end)}`
}

/** Er perioden overstået, i gang eller på vej? Bruges til at gruppere listen. */
function phaseOf(absence: Absence, now: string): 'now' | 'coming' | 'past' {
  if (absence.endDate < now) return 'past'
  if (absence.startDate > now) return 'coming'
  return 'now'
}

/**
 * Fravær og ferie (beslutning 053).
 *
 * Siden svarer på ét spørgsmål: hvem er der ikke, og hvornår. Der er ingen
 * godkendelse og ingen feriesaldi - ferieregnskabet hører hjemme i lønsystemet,
 * på samme måde som timerne gør (beslutning 046).
 *
 * Alle må se listen; en projektleder må registrere for alle, en medarbejder kun
 * for sig selv. Det er med vilje de samme regler som i kalenderen.
 */
export function AbsencesPage() {
  const now = today()
  const { user, isProjectManager } = useAuth()

  // Vinduet er bredt med vilje: man planlægger sommerferie i februar.
  const [from, setFrom] = useState(() => addDays(now, -30))
  const [to, setTo] = useState(() => addDays(now, 365))
  const [editing, setEditing] = useState<Absence | null | undefined>(undefined)

  const { data, isLoading, error } = useAbsences(from, to)
  const remove = useDeleteAbsence()

  const groups = useMemo(() => {
    const rows = data ?? []
    return {
      now: rows.filter((a) => phaseOf(a, now) === 'now'),
      coming: rows.filter((a) => phaseOf(a, now) === 'coming'),
      past: rows.filter((a) => phaseOf(a, now) === 'past').reverse()
    }
  }, [data, now])

  const canEdit = (absence: Absence) => isProjectManager || absence.user.id === user?.id

  async function handleDelete(absence: Absence) {
    const who = absence.user.displayName
    if (!confirm(`Slet ${absence.typeName.toLowerCase()} for ${who} ${formatPeriod(absence.startDate, absence.endDate)}?`)) {
      return
    }
    await remove.mutateAsync(absence.id)
  }

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {groups.now.length > 0
            ? `${groups.now.length} væk i dag`
            : 'Ingen væk i dag'}
        </div>

        <label className="cal-person">
          Fra
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="cal-person">
          Til
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>

        <button className="btn btn--primary btn--sm" onClick={() => setEditing(null)}>
          Registrér fravær
        </button>
      </div>

      {error && <ErrorBox error={error} />}
      {isLoading && <Loading />}

      {data && data.length === 0 && (
        <EmptyState>
          Der står intet fravær i perioden. Tryk <strong>Registrér fravær</strong> for at skrive ferie,
          sygdom eller kursus ind - så kan man se det, før man planlægger.
        </EmptyState>
      )}

      {groups.now.length > 0 && (
        <AbsenceList
          title="Væk lige nu"
          rows={groups.now}
          canEdit={canEdit}
          onEdit={setEditing}
          onDelete={handleDelete}
        />
      )}

      {groups.coming.length > 0 && (
        <AbsenceList
          title="På vej"
          rows={groups.coming}
          canEdit={canEdit}
          onEdit={setEditing}
          onDelete={handleDelete}
        />
      )}

      {groups.past.length > 0 && (
        <AbsenceList
          title="Overstået"
          rows={groups.past}
          canEdit={canEdit}
          onEdit={setEditing}
          onDelete={handleDelete}
          muted
        />
      )}

      {editing !== undefined && (
        <AbsenceDialog absence={editing} onClose={() => setEditing(undefined)} />
      )}
    </>
  )
}

function AbsenceList({
  title,
  rows,
  canEdit,
  onEdit,
  onDelete,
  muted
}: {
  title: string
  rows: Absence[]
  canEdit: (a: Absence) => boolean
  onEdit: (a: Absence) => void
  onDelete: (a: Absence) => void
  muted?: boolean
}) {
  return (
    <Card title={`${title} (${rows.length})`}>
      <div className="stack" style={{ gap: 8, opacity: muted ? 0.7 : 1 }}>
        {rows.map((a) => (
          <div key={a.id} className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span
              title={a.typeName}
              style={{
                width: 6,
                alignSelf: 'stretch',
                minHeight: 34,
                borderRadius: 3,
                background: a.color,
                flexShrink: 0
              }}
            />
            <Avatar user={a.user} size={30} />
            <div style={{ minWidth: 150, flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{a.user.displayName}</div>
              <div className="small muted">
                {a.typeName} · {formatPeriod(a.startDate, a.endDate)} · {a.dayCount}{' '}
                {a.dayCount === 1 ? 'dag' : 'dage'}
              </div>
              {a.note && <div className="small">{a.note}</div>}
            </div>

            {canEdit(a) && (
              <div className="row" style={{ gap: 6 }}>
                <button className="btn btn--sm" onClick={() => onEdit(a)}>
                  Ret
                </button>
                <button className="btn btn--danger btn--sm" onClick={() => onDelete(a)}>
                  Slet
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * Opret eller ret. En medarbejder kan kun vælge sig selv - listen viser derfor
 * kun ham, i stedet for at lade ham vælge en kollega og først få et nej bagefter.
 */
function AbsenceDialog({ absence, onClose }: { absence: Absence | null; onClose: () => void }) {
  const { user, isProjectManager } = useAuth()
  const { data: users } = useUsers()
  const { data: types } = useAbsenceTypes()
  const save = useSaveAbsence()

  const [form, setForm] = useState<AbsenceInput>({
    userId: absence?.user.id ?? user?.id ?? 0,
    type: absence?.type ?? 'vacation',
    startDate: absence?.startDate ?? today(),
    endDate: absence?.endDate ?? today(),
    note: absence?.note ?? ''
  })
  const [error, setError] = useState<string | null>(null)

  const selectableUsers = isProjectManager
    ? (users ?? []).filter((u) => u.isActive)
    : (users ?? []).filter((u) => u.id === user?.id)

  const days =
    form.endDate >= form.startDate
      ? Math.round(
          (new Date(form.endDate).getTime() - new Date(form.startDate).getTime()) / 86400000
        ) + 1
      : 0

  async function submit() {
    setError(null)
    try {
      await save.mutateAsync({
        id: absence?.id,
        body: { ...form, note: form.note?.trim() || null }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme fraværet.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>
          {absence ? 'Ret fravær' : 'Registrér fravær'}
        </h2>

        <label className="field">
          <span className="field__label">Hvem</span>
          <select
            value={form.userId}
            disabled={!isProjectManager}
            onChange={(e) => setForm({ ...form, userId: Number(e.target.value) })}
          >
            {selectableUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Hvad</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {(types ?? []).map((t) => (
              <option key={t.code} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
        </label>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Første dag</span>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => {
                const startDate = e.target.value
                // Slutdatoen følger med frem, så den aldrig kommer til at ligge før starten.
                setForm({
                  ...form,
                  startDate,
                  endDate: form.endDate < startDate ? startDate : form.endDate
                })
              }}
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Sidste dag</span>
            <input
              type="date"
              value={form.endDate}
              min={form.startDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </label>
        </div>

        <p className="small muted" style={{ marginTop: 0 }}>
          {days > 0 ? `${days} ${days === 1 ? 'dag' : 'dage'}, begge dage talt med.` : 'Vælg en periode.'}
        </p>

        <label className="field">
          <span className="field__label">Note (valgfri)</span>
          <input
            value={form.note ?? ''}
            placeholder="F.eks. kursussted, eller et nummer man kan nås på"
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </label>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            className="btn btn--primary"
            disabled={save.isPending || days <= 0 || !form.userId}
            onClick={() => void submit()}
          >
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" onClick={onClose}>
            Annullér
          </button>
        </div>
      </div>
    </div>
  )
}
